import { useCallback, useEffect, useRef, useState } from 'react';
import type { Classifier } from '@zg/shared/property';
import { saveDraft, PropertyApiError } from '../lib/propertyApi';
import {
  clearDraftId,
  loadDraftId,
  saveDraftId,
} from '../lib/propertyStorage';

const DEBOUNCE_MS = 1500;

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UsePropertyDraftResult {
  status: DraftSaveStatus;
  lastSavedAt: string | null;
  draftId: string | null;
  magicLinkSent: boolean;
  resumeToken: string | null;
  /** Imperative save; used by the MagicLinkPrompt when user provides email. */
  saveNow: (opts?: { email?: string }) => Promise<void>;
  error: string | null;
}

/**
 * Autosave the current classifier + answers to the property-api every
 * DEBOUNCE_MS ms after the last change. The first save creates the draft
 * row in DynamoDB; subsequent saves update it in place.
 *
 * Magic-link email capture is handled by the caller via `saveNow({ email })`
 * — see MagicLinkPrompt.tsx.
 */
export function usePropertyDraft(
  classifier: Classifier | null,
  answers: Record<string, unknown>
): UsePropertyDraftResult {
  const [status, setStatus] = useState<DraftSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [draftId, setDraftIdState] = useState<string | null>(() => loadDraftId());
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resumeToken, setResumeToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflight = useRef<Promise<void> | null>(null);

  const doSave = useCallback(
    async (overrides?: { email?: string }): Promise<void> => {
      if (!classifier) return;
      setStatus('saving');
      setError(null);
      async function trySave(useDraftId: string | undefined) {
        return saveDraft({
          draftId: useDraftId,
          email: overrides?.email,
          classifier: classifier as Classifier,
          answers,
        });
      }
      try {
        let res;
        try {
          res = await trySave(draftId ?? undefined);
        } catch (e) {
          // If the draft vanished (TTL'd, deleted, new client session with a
          // stale draftId in sessionStorage), drop the id and retry as a fresh
          // create. This keeps autosave resilient to edge cases without
          // user-visible failures.
          if (
            e instanceof PropertyApiError &&
            e.code === 'draft_not_found' &&
            draftId
          ) {
            setDraftIdState(null);
            clearDraftId();
            res = await trySave(undefined);
          } else {
            throw e;
          }
        }
        setDraftIdState(res.draftId);
        saveDraftId(res.draftId);
        if (res.token) setResumeToken(res.token);
        if (res.magicLinkSent) setMagicLinkSent(true);
        setLastSavedAt(new Date().toISOString());
        setStatus('saved');
      } catch (err) {
        const msg =
          err instanceof PropertyApiError
            ? `${err.code}${err.status ? ` (${err.status})` : ''}`
            : err instanceof Error
              ? err.message
              : String(err);
        setError(msg);
        setStatus('error');
      } finally {
        inflight.current = null;
      }
    },
    [classifier, answers, draftId]
  );

  // Debounced autosave on answers change.
  useEffect(() => {
    if (!classifier) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // Skip if there's nothing to save (empty answers + no draftId yet).
      if (!draftId && Object.keys(answers).length === 0) return;
      if (inflight.current) return;
      inflight.current = doSave();
    }, DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [classifier, answers, draftId, doSave]);

  const saveNow = useCallback(
    async (opts?: { email?: string }) => {
      if (inflight.current) await inflight.current;
      inflight.current = doSave(opts);
      return inflight.current;
    },
    [doSave]
  );

  return {
    status,
    lastSavedAt,
    draftId,
    magicLinkSent,
    resumeToken,
    saveNow,
    error,
  };
}
